/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import { css, styled, jsx } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup as AkDropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import EditIcon from '@atlaskit/icon/core/migration/edit';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import { useRouter, useRouterActions, generatePath } from '@atlassian/react-resource-router';
import { PLANS_BASE_PATH, SECTION_ID } from '../../../../common/constants.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const SIDEBAR_PADDING = 28;

export const DropMenuItem = AnalyticsEventToProps('dropMenuItem', {
	onClick: 'clicked',
})(DropdownItem);

const noPaddingStyles = css({
	'&::before': { display: 'none' },
	'&::after': { display: 'none' },
});

const ScenarioSwitcher = ({ planNavInfo, scenario, outlineColor }: Props) => {
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();
	const { push } = useRouterActions();
	const { width } = useLeftSidebarState();
	const [isOpen, setIsOpen] = useState(false);
	const { scenarios } = planNavInfo;

	const {
		path,
		params: { planId, incrementId },
	} = match;

	const handleOnClick = useCallback(
		(scenarioId: number, nextPage?: string) => {
			const params = { planId, scenarioId, incrementId };

			const url = nextPage
				? `${generatePath(PLANS_BASE_PATH, params)}/${nextPage}`
				: generatePath(path, params);

			push(url);
		},
		[incrementId, path, planId, push],
	);

	return (
		<DropdownMenu
			isOpen={isOpen}
			onOpenChange={(attrs: OnOpenChangeArgs) => {
				setIsOpen(attrs.isOpen);
			}}
			trigger={({ triggerRef, ...props }) => (
				<TriggerContainer>
					<Button
						{...props}
						ref={triggerRef}
						shouldFitContainer
						iconAfter={<ChevronDownIcon label="" />}
					>
						<ScenarioTitleContainer>
							<ScenarioItemColor backgroundColor={scenario.color} borderColor={outlineColor} />
							<DropMenuItemText>{scenario.title}</DropMenuItemText>
						</ScenarioTitleContainer>
					</Button>
				</TriggerContainer>
			)}
		>
			<DropdownItemGroup width={width - SIDEBAR_PADDING} title={formatMessage(messages.header)}>
				{scenarios.map(({ color, id, title }) => (
					<DropMenuItem
						key={id}
						elemBefore={<ScenarioItemColor backgroundColor={color} />}
						id={id}
						isSelected={id === scenario.id}
						onClick={() => {
							handleOnClick(id);
						}}
					>
						<DropMenuItemText>{title}</DropMenuItemText>
					</DropMenuItem>
				))}
			</DropdownItemGroup>
			<DropdownItemGroup
				width={width - SIDEBAR_PADDING}
				hasSeparator
				// @ts-expect-error Property 'css' does not exist on type 'IntrinsicAttributes & { width: number; } & SectionProps & RefAttributes<HTMLElement> & StyledProps'.ts(2322)
				css={noPaddingStyles}
			>
				<DropMenuItem
					elemBefore={<EditIcon spacing="spacious" label="" />}
					onClick={() => {
						handleOnClick(scenario.id, `${SECTION_ID.SETTINGS}/scenarios`);
					}}
				>
					<DropMenuItemText>{formatMessage(messages.manageScenarios)}</DropMenuItemText>
				</DropMenuItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export default ScenarioSwitcher;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScenarioItemColor = styled.div<{ backgroundColor: string; borderColor?: string }>({
	borderRadius: '3px',
	height: '16px',
	width: '16px',
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.backgroundColor,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) => (props.borderColor ? `1px solid ${props.borderColor}` : 'none'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[aria-expanded='true']": {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.selected'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.hovered'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			color: `${token('color.text')} !important`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScenarioTitleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		marginRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemGroup = styled(AkDropdownItemGroup)<{ width: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > button span:last-child': {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropMenuItemText = styled.span({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});
